import { CommandListPricesheets } from "./commands/cmdListPricesheets";
import { CommandGetPricesheet } from "./commands/cmdGetPricesheet";
import { CommandCreatePricesheet, CreatePricesheetPayload } from "./commands/cmdCreatePricesheet";
import { CommandUpdatePricesheet, UpdatePricesheetPayload } from "./commands/cmdUpdatePricesheet";
import { CommandDeletePricesheet } from "./commands/cmdDeletePricesheet";
import { CommandListPricesheetItems } from "./commands/cmdListPricesheetItems";
import { CommandCreatePricesheetItem, CreatePricesheetItemPayload } from "./commands/cmdCreatePricesheetItem";
import { CommandUpdatePricesheetItem, UpdatePricesheetItemPayload } from "./commands/cmdUpdatePricesheetItem";
import { CommandDeletePricesheetItem } from "./commands/cmdDeletePricesheetItem";
import { CommandListJourneyPaths } from "./commands/cmdListJourneyPaths";
import { CommandListContractJourneyPaths } from "./commands/cmdListContractJourneyPaths";
import { CommandCreateJourneyPath } from "./commands/cmdCreateJourneyPath";
import { CommandUpdateJourneyPath, UpdateJourneyPathPayload } from "./commands/cmdUpdateJourneyPath";
import { CommandDeleteJourneyPath } from "./commands/cmdDeleteJourneyPath";
import { CommandListObservedDimensions, type JourneyStepDimensionOption } from "./commands/cmdListObservedDimensions";
import { Pricesheet } from "@models/pricesheet.model";
import { PricesheetItem } from "@models/pricesheetitem.model";
import { JourneyPath } from "@models/journeyPath.model";

export class PricesheetService {
  async list(contractId: number, params?: {
    is_active?: boolean;
  }): Promise<Pricesheet[]> {
    try {
      const cmd = new CommandListPricesheets();
      return await cmd.run(contractId, params);
    } catch (error) {
      console.error("Failed to fetch pricesheets:", error);
      return [];
    }
  }

  async get(contractId: number, pricesheetId: number): Promise<Pricesheet | null> {
    try {
      const cmd = new CommandGetPricesheet();
      return await cmd.run(contractId, pricesheetId);
    } catch (error) {
      console.error(`Failed to fetch pricesheet ${pricesheetId}:`, error);
      return null;
    }
  }

  async create(contractId: number, payload: CreatePricesheetPayload): Promise<Pricesheet> {
    try {
      const cmd = new CommandCreatePricesheet();
      return await cmd.run(contractId, payload);
    } catch (error) {
      console.error("Failed to create pricesheet:", error);
      throw error;
    }
  }

  async update(contractId: number, pricesheetId: number, payload: UpdatePricesheetPayload): Promise<Pricesheet> {
    try {
      const cmd = new CommandUpdatePricesheet();
      return await cmd.run(contractId, pricesheetId, payload);
    } catch (error) {
      console.error(`Failed to update pricesheet ${pricesheetId}:`, error);
      throw error;
    }
  }

  async delete(contractId: number, pricesheetId: number): Promise<Pricesheet> {
    try {
      const cmd = new CommandDeletePricesheet();
      return await cmd.run(contractId, pricesheetId);
    } catch (error) {
      console.error(`Failed to delete pricesheet ${pricesheetId}:`, error);
      throw error;
    }
  }

  async listItems(contractId: number, pricesheetId: number): Promise<PricesheetItem[]> {
    try {
      const cmd = new CommandListPricesheetItems();
      return await cmd.run(contractId, pricesheetId);
    } catch (error) {
      console.error(`Failed to fetch items for pricesheet ${pricesheetId}:`, error);
      return [];
    }
  }

  async createItem(contractId: number, pricesheetId: number, payload: CreatePricesheetItemPayload): Promise<PricesheetItem> {
    try {
      const cmd = new CommandCreatePricesheetItem();
      return await cmd.run(contractId, pricesheetId, payload);
    } catch (error) {
      console.error("Failed to create pricesheet item:", error);
      throw error;
    }
  }

  async updateItem(contractId: number, pricesheetId: number, itemId: number, payload: UpdatePricesheetItemPayload): Promise<PricesheetItem> {
    try {
      const cmd = new CommandUpdatePricesheetItem();
      return await cmd.run(contractId, pricesheetId, itemId, payload);
    } catch (error) {
      console.error(`Failed to update pricesheet item ${itemId}:`, error);
      throw error;
    }
  }

  async deleteItem(contractId: number, pricesheetId: number, itemId: number): Promise<PricesheetItem> {
    try {
      const cmd = new CommandDeletePricesheetItem();
      return await cmd.run(contractId, pricesheetId, itemId);
    } catch (error) {
      console.error(`Failed to delete pricesheet item ${itemId}:`, error);
      throw error;
    }
  }

  async listPaths(contractId: number, pricesheetId: number): Promise<JourneyPath[]> {
    try {
      return await new CommandListJourneyPaths().run(contractId, pricesheetId);
    } catch (error) {
      console.error(`Failed to fetch pricing groups for pricesheet ${pricesheetId}:`, error);
      return [];
    }
  }

  async listContractPaths(contractId: number): Promise<JourneyPath[]> {
    try {
      return await new CommandListContractJourneyPaths().run(contractId);
    } catch (error) {
      console.error(`Failed to fetch pricing groups for contract ${contractId}:`, error);
      return [];
    }
  }

  async createPath(
    contractId: number,
    pricesheetId: number,
    name: string,
    populationSegmentId: number | null = null,
  ): Promise<JourneyPath> {
    return await new CommandCreateJourneyPath().run(contractId, pricesheetId, name, populationSegmentId);
  }

  async updatePath(
    contractId: number,
    pricesheetId: number,
    pathId: number,
    payload: UpdateJourneyPathPayload,
  ): Promise<JourneyPath> {
    return await new CommandUpdateJourneyPath().run(contractId, pricesheetId, pathId, payload);
  }

  async deletePath(contractId: number, pricesheetId: number, pathId: number): Promise<JourneyPath> {
    return await new CommandDeleteJourneyPath().run(contractId, pricesheetId, pathId);
  }

  async listObservedDimensions(contractId: number, pricesheetId: number, stepId: number): Promise<JourneyStepDimensionOption[]> {
    try {
      return await new CommandListObservedDimensions().run(contractId, pricesheetId, stepId);
    } catch (error) {
      console.error(`Failed to fetch observed dimensions for step ${stepId}:`, error);
      return [];
    }
  }
}
